import * as signalR from '@microsoft/signalr';

export interface NotificationData {
  id: string;
  title: string;
  message: string;
  leadId?: string;
  conversationId?: string;
  type: string;
  isRead: boolean;
  createdAt: string;
}

export interface ChatNotificationData {
  agentId: string;
  userPhone: string;
  message: string;
  chatId: string;
  chatName: string;
  timestamp: string;
}

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private notificationCallbacks: ((notification: NotificationData) => void)[] = [];
  private chatNotificationCallbacks: ((chatNotification: ChatNotificationData) => void)[] = [];
  private isStarting = false;

  async startConnection(token?: string): Promise<void> {
    if (this.isConnected || this.isStarting) {
      return;
    }

    try {
      this.isStarting = true;
      console.log('🔐 Starting SignalR connection with token:', token ? 'Present' : 'Missing');
      const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${baseUrl}/notificationHub`, {
          accessTokenFactory: () => {
            console.log('🎟️ Token factory called, token:', token ? 'Present' : 'Missing');
            return token || '';
          }
        })
        .withAutomaticReconnect([0, 2000, 10000, 30000])
        .build();

      this.connection.on('ReceiveNotification', (notification: NotificationData) => {
        console.log('🔔 SignalR Notification received:', notification);
        console.log('📊 Number of callbacks registered:', this.notificationCallbacks.length);
        this.notificationCallbacks.forEach(callback => callback(notification));
      });

      this.connection.on('ReceiveChatNotification', (chatNotification: ChatNotificationData) => {
        console.log('💬 SignalR Chat Notification received:', chatNotification);
        console.log('📊 Number of chat callbacks registered:', this.chatNotificationCallbacks.length);
        this.chatNotificationCallbacks.forEach(callback => callback(chatNotification));
      });

      this.connection.onreconnecting(() => {
        console.log('SignalR reconnecting...');
      });

      this.connection.onreconnected(() => {
        console.log('SignalR reconnected');
        this.reconnectAttempts = 0;
      });

      this.connection.onclose(() => {
        console.log('SignalR connection closed');
        this.isConnected = false;
        
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          setTimeout(() => {
            if (token) {
              this.startConnection(token);
            }
          }, 5000 * this.reconnectAttempts);
        }
      });

      await this.connection.start();
      console.log('SignalR connection established');
      this.isConnected = true;
      this.reconnectAttempts = 0;
    } catch (error) {
      console.error('Error starting SignalR connection:', error);
      this.isConnected = false;
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        setTimeout(() => {
          if (token) {
            this.startConnection(token);
          }
        }, 5000 * this.reconnectAttempts);
      }
    } finally {
      this.isStarting = false;
    }

    // Ensure single handler instance
    if (this.connection) {
      this.connection.off('ReceiveNotification');
      this.connection.off('ReceiveChatNotification');
      this.connection.on('ReceiveNotification', (notification: NotificationData) => {
        console.log('🔔 SignalR Notification received:', notification);
        console.log('📊 Number of callbacks registered:', this.notificationCallbacks.length);
        this.notificationCallbacks.forEach(callback => callback(notification));
      });
      this.connection.on('ReceiveChatNotification', (chatNotification: ChatNotificationData) => {
        console.log('💬 SignalR Chat Notification received:', chatNotification);
        console.log('📊 Number of chat callbacks registered:', this.chatNotificationCallbacks.length);
        this.chatNotificationCallbacks.forEach(callback => callback(chatNotification));
      });
    }
  }

  async stopConnection(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.error('Error stopping SignalR connection:', error);
      } finally {
        this.isConnected = false;
        this.connection = null;
      }
    }
  }

  onNotification(callback: (notification: NotificationData) => void): void {
    if (!this.notificationCallbacks.includes(callback)) {
      this.notificationCallbacks.push(callback);
    }
  }

  offNotification(callback: (notification: NotificationData) => void): void {
    const index = this.notificationCallbacks.indexOf(callback);
    if (index > -1) {
      this.notificationCallbacks.splice(index, 1);
    }
  }

  onChatNotification(callback: (chatNotification: ChatNotificationData) => void): void {
    if (!this.chatNotificationCallbacks.includes(callback)) {
      this.chatNotificationCallbacks.push(callback);
    }
  }

  offChatNotification(callback: (chatNotification: ChatNotificationData) => void): void {
    const index = this.chatNotificationCallbacks.indexOf(callback);
    if (index > -1) {
      this.chatNotificationCallbacks.splice(index, 1);
    }
  }

  getConnectionState(): signalR.HubConnectionState | null {
    return this.connection?.state || null;
  }

  isConnectionActive(): boolean {
    return this.isConnected && this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

export const signalRService = new SignalRService();